import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { ApiService } from 'src/app/shared/services/api.service';
import { LrsService, LrsRoute } from 'src/app/shared/services/lrs.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

interface SurveyBoqLine {
  item_id: number;
  item_code?: string;
  item_description?: string;
  unit?: string;
  estimated_qty: number;
  surveyed_qty: number;
}

interface SurveyDocument {
  name: string;
  url: string;
  uploaded_at: string;
}

interface SurveyProjectOption {
  id: number;
  project_name: string;
  domain?: string;
}

@Component({
  selector: 'app-add-survey',
  templateUrl: './add-survey.component.html',
  styleUrl: './add-survey.component.css',
})
export class AddSurveyComponent implements OnInit {
  surveyForm!: FormGroup;
  selectedDomain = 'Residential';
  domainOptions: string[] = [
    'Residential', 'Commercial', 'Educational', 'Water', 'Power', 'Gas', 'Telecom', 'Road', 'Consultancy',
  ];
  optionsMap: { [key: string]: { value: string | number; label: string }[] } = {
    domainOptionsDrp: this.domainOptions.map((d) => ({ value: d, label: d })),
    projectOptions: [],
  };
  projects: SurveyProjectOption[] = [];
  recordId: number | null = null;
  saving = false;
  uploading = false;
  lrsRoutes: LrsRoute[] = [];
  queryProjectId: string | null = null;
  queryProjectName = '';
  existingRemarks: Record<string, unknown> = {};
  documents: SurveyDocument[] = [];
  boqLines: SurveyBoqLine[] = [];
  boqComparison: SurveyBoqLine[] = [];
  boqCode: string | null = null;
  noBoq = false;

  domainFields: { [key: string]: { key: string; label: string; type: string; required?: boolean; value?: unknown }[] } = {
    Residential: [
      { key: 'plotBoundaries', label: 'Plot Boundaries', type: 'text', value: '' },
      { key: 'soilComposition', label: 'Soil Composition', type: 'text', value: '' },
      { key: 'elevationProfile', label: 'Elevation Profile', type: 'text', value: '' },
    ],
    Commercial: [
      { key: 'utilityAccessPoints', label: 'Utility Access Points', type: 'text', value: '' },
      { key: 'trafficDensitySurvey', label: 'Traffic Density Survey', type: 'text', value: '' },
      { key: 'noiseLevelAssessment', label: 'Noise Level Assessment', type: 'text', value: '' },
    ],
    Educational: [
      { key: 'accessibilityMapping', label: 'Accessibility Mapping', type: 'text', value: '' },
      { key: 'environmentalImpact', label: 'Environmental Impact', type: 'text', value: '' },
      { key: 'landGradient', label: 'Land Gradient', type: 'number', value: '' },
    ],
    Water: [
      { key: 'waterTableDepth', label: 'Water Table Depth', type: 'text', value: '' },
      { key: 'sourceIdentification', label: 'Source Identification', type: 'text', value: '' },
      { key: 'flowRateMeasurement', label: 'Flow Rate Measurement', type: 'text', value: '' },
    ],
    Power: [
      { key: 'gridProximity', label: 'Grid Proximity', type: 'text', value: '' },
      { key: 'terrainAnalysis', label: 'Terrain Analysis', type: 'text', value: '' },
      { key: 'emfSurvey', label: 'EMF Survey', type: 'text', value: '' },
    ],
    Gas: [
      { key: 'pipelineCorridorWidth', label: 'Pipeline Corridor Width', type: 'text', value: '' },
      { key: 'soilStabilityIndex', label: 'Soil Stability Index', type: 'text', value: '' },
      { key: 'leakRiskZones', label: 'Leak Risk Zones', type: 'text', value: '' },
    ],
    Telecom: [
      { key: 'signalInterferenceZones', label: 'Signal Interference Zones', type: 'text', value: '' },
      { key: 'towerBaseCoordinates', label: 'Tower Base Coordinates', type: 'text', value: '' },
      { key: 'fiberRouteFeasibility', label: 'Fiber Route Feasibility', type: 'text', value: '' },
    ],
    Road: [
      { key: 'pavementConditionIndex', label: 'Pavement Condition Index', type: 'number', value: '' },
      { key: 'rightOfWayWidth', label: 'Right-of-Way Width', type: 'text', value: '' },
      { key: 'trafficSurveyPoints', label: 'Traffic Survey Points', type: 'number', value: '' },
    ],
    Consultancy: [
      { key: 'surveyScopeDefinition', label: 'Survey Scope Definition', type: 'text', value: '' },
      { key: 'clientSpecificMetrics', label: 'Client-Specific Metrics', type: 'text', value: '' },
      { key: 'surveyReviewTimeline', label: 'Survey Review Timeline', type: 'date', value: '' },
    ],
  };

  commonFields = [
    {
      key: 'projectId',
      label: 'Project',
      type: 'select',
      optionKey: 'projectOptions',
      required: true,
      value: '',
      onChange: (value: string | number) => this.onProjectChange(value),
    },
    { key: 'surveyLead', label: 'Survey Lead', type: 'text', required: true, value: '' },
    { key: 'surveyStartDate', label: 'Survey Start Date', type: 'date', required: true, value: '' },
    { key: 'surveyEndDate', label: 'Survey End Date', type: 'date', required: true, value: '' },
    { key: 'surveyType', label: 'Survey Type', type: 'text', required: true, value: '' },
    { key: 'equipmentUsed', label: 'Equipment Used', type: 'text', required: true, value: '' },
    { key: 'dataCollectionMethod', label: 'Data Collection Method', type: 'text', required: true, value: '' },
    { key: 'accuracyLevel', label: 'Accuracy Level', type: 'hidden', required: false, value: '' },
    { key: 'surveyReportReference', label: 'Survey Report Reference', type: 'text', required: true, value: '' },
    {
      key: 'domain',
      label: 'Domain',
      type: 'select',
      optionKey: 'domainOptionsDrp',
      required: true,
      value: 'Residential',
      onChange: (value: string) => this.onDomainChange(value),
    },
  ];

  pickerMap: { [key: string]: MatDatepicker<unknown> } = {};

  constructor(
    private fb: FormBuilder,
    private readonly apiService: ApiService,
    private readonly lrsService: LrsService,
    private readonly toastrMsgService: ToastrMsgService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.surveyForm = this.fb.group({});
    this.commonFields.forEach((field) => {
      const validators = field.required ? [Validators.required] : [];
      this.surveyForm.addControl(field.key, this.fb.control(field.value ?? '', validators));
    });
    this.onDomainChange(this.selectedDomain);
    this.surveyForm.addControl('projectName', this.fb.control(''));
    this.surveyForm.addControl('lrsRouteId', this.fb.control<number | null>(null));
    this.surveyForm.addControl('lrsFromKm', this.fb.control<number | null>(null));
    this.surveyForm.addControl('lrsToKm', this.fb.control<number | null>(null));
    this.surveyForm.addControl('lrsChainage', this.fb.control(''));

    this.loadProjects();

    this.route.queryParams.subscribe((params) => {
      const projectId = params['projectId'] || params['project_id'];
      const projectName = params['projectName'];
      this.queryProjectId = projectId || null;
      this.queryProjectName = projectName || '';
      if (projectId && this.surveyForm) {
        this.surveyForm.patchValue({
          projectId: Number(projectId),
          projectName: projectName || '',
        });
        this.applyPendingProject(!this.recordId);
      }
    });

    this.route.params.subscribe((params) => {
      const id = params['id'];
      if (id) {
        this.recordId = Number(id);
        this.surveyForm.get('projectId')?.disable({ emitEvent: false });
        this.loadRecord(this.recordId);
      }
    });
  }

  private loadRecord(id: number): void {
    this.apiService.getProjectSurveyStageById(id).subscribe({
      next: (res: { data?: Record<string, unknown> }) => {
        const row = res.data || (res as Record<string, unknown>);
        const remarks = this.parseRemarks(row['remarks']);
        this.existingRemarks = remarks;
        this.documents = this.parseDocuments(row['documents']);
        this.surveyForm.patchValue({
          projectName: row['project_name'],
          projectId: row['project_id'] != null ? Number(row['project_id']) : '',
          surveyLead: row['survey_lead'],
          surveyStartDate: row['survey_start_date'],
          surveyEndDate: row['survey_end_date'],
          surveyType: remarks['surveyType'],
          equipmentUsed: remarks['equipmentUsed'],
          dataCollectionMethod: remarks['dataCollectionMethod'],
          accuracyLevel: remarks['accuracyLevel'],
          surveyReportReference: remarks['surveyReportReference'],
          domain: row['domain'] || 'Residential',
        });
        this.onDomainChange(String(row['domain'] || 'Residential'));
        if (remarks['domainFields']) {
          this.surveyForm.patchValue(remarks['domainFields'] as Record<string, unknown>);
        }
        const lrs = (remarks['lrs'] as Record<string, unknown>) || {};
        this.surveyForm.patchValue({
          lrsRouteId: lrs['route_id'] != null ? Number(lrs['route_id']) : null,
          lrsFromKm: lrs['from_km'] != null ? Number(lrs['from_km']) : null,
          lrsToKm: lrs['to_km'] != null ? Number(lrs['to_km']) : null,
          lrsChainage: lrs['chainage'] || '',
        });
        const comparison = remarks['boq_comparison'];
        if (Array.isArray(comparison)) {
          this.boqComparison = comparison as SurveyBoqLine[];
        }
        const projectId = row['project_id'] != null ? String(row['project_id']) : undefined;
        this.queryProjectId = this.queryProjectId || projectId || null;
        this.queryProjectName = this.queryProjectName || String(row['project_name'] || '');
        this.applyPendingProject(false);
        this.surveyForm.get('projectId')?.disable({ emitEvent: false });
      },
      error: (err) => this.toastrMsgService.error(err?.error?.message || 'Failed to load survey'),
    });
  }

  private parseRemarks(raw: unknown): Record<string, unknown> {
    if (!raw) return {};
    if (typeof raw === 'object' && !Array.isArray(raw)) return { ...(raw as Record<string, unknown>) };
    try {
      const parsed = JSON.parse(String(raw));
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return { legacy_remarks: raw };
    }
  }

  private parseDocuments(raw: unknown): SurveyDocument[] {
    const list = Array.isArray(raw) ? raw : this.parseJsonArray(raw);
    return list
      .map((doc) => {
        if (!doc || typeof doc !== 'object') return null;
        const item = doc as Record<string, unknown>;
        const url = String(item['url'] || item['file_url'] || '');
        if (!url) return null;
        return {
          name: String(item['name'] || item['file_name'] || 'Report'),
          url,
          uploaded_at: String(item['uploaded_at'] || ''),
        };
      })
      .filter((doc): doc is SurveyDocument => !!doc);
  }

  private parseJsonArray(raw: unknown): unknown[] {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(String(raw));
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private loadBoqLines(projectId: string): void {
    this.apiService.getBoQs({ project_id: projectId, limit: 20 }).subscribe({
      next: (res: { data?: Array<Record<string, unknown>> }) => {
        const list = [...(res?.data || [])].sort((a, b) => Number(b['version'] || 0) - Number(a['version'] || 0));
        const latest = list[0];
        if (!latest?.['boq_id']) {
          this.noBoq = true;
          this.boqLines = [];
          return;
        }
        this.noBoq = false;
        this.boqCode = String(latest['boq_code'] || '');
        this.apiService.getBoQById(Number(latest['boq_id'])).subscribe({
          next: (detail: { data?: Record<string, unknown> }) => {
            const boq = detail?.data || {};
            let items: Array<Record<string, unknown>> = [];
            const sections = Array.isArray(boq['sections']) ? (boq['sections'] as Array<Record<string, unknown>>) : [];
            sections.forEach((section) => {
              if (Array.isArray(section['items'])) {
                items = items.concat(section['items'] as Array<Record<string, unknown>>);
              }
            });
            if (Array.isArray(boq['itemsWithoutSection'])) {
              items = items.concat(boq['itemsWithoutSection'] as Array<Record<string, unknown>>);
            }
            if (!items.length && Array.isArray(boq['items'])) {
              items = boq['items'] as Array<Record<string, unknown>>;
            }
            const saved = Array.isArray(this.existingRemarks['surveyed_quantities'])
              ? (this.existingRemarks['surveyed_quantities'] as SurveyBoqLine[])
              : [];
            this.boqLines = items.map((item) => {
              const match = saved.find(
                (row) => Number(row.item_id) === Number(item['item_id']) || (row.item_code && row.item_code === item['item_code'])
              );
              const estimated = Number(item['quantity']) || 0;
              return {
                item_id: Number(item['item_id']),
                item_code: item['item_code'] != null ? String(item['item_code']) : undefined,
                item_description: item['item_description'] != null ? String(item['item_description']) : undefined,
                unit: item['unit'] != null ? String(item['unit']) : undefined,
                estimated_qty: estimated,
                surveyed_qty: match?.surveyed_qty != null ? Number(match.surveyed_qty) : estimated,
              };
            });
          },
          error: () => {
            this.noBoq = true;
            this.boqLines = [];
          },
        });
      },
      error: () => {
        this.noBoq = true;
        this.boqLines = [];
      },
    });
  }

  private buildPayload(): Record<string, unknown> {
    const v = this.surveyForm.getRawValue();
    const domainKeys = (this.domainFields[this.selectedDomain] || []).map((f) => f.key);
    const domainFields: Record<string, unknown> = {};
    domainKeys.forEach((k) => {
      if (v[k] !== undefined && v[k] !== '') domainFields[k] = v[k];
    });
    const remarks: Record<string, unknown> = {
      ...this.existingRemarks,
      surveyType: v.surveyType,
      equipmentUsed: v.equipmentUsed,
      dataCollectionMethod: v.dataCollectionMethod,
      accuracyLevel: v.accuracyLevel,
      surveyReportReference: v.surveyReportReference,
      domainFields,
      surveyed_quantities: this.boqLines.map((line) => ({
        item_id: line.item_id,
        item_code: line.item_code,
        surveyed_qty: Number(line.surveyed_qty),
      })),
      lrs: this.isRoadDomain && v.lrsRouteId
        ? {
            route_id: v.lrsRouteId,
            from_km: v.lrsFromKm,
            to_km: v.lrsToKm,
            chainage: v.lrsChainage || undefined,
          }
        : this.existingRemarks['lrs'],
    };
    const payload: Record<string, unknown> = {
      project_id: v.projectId || this.queryProjectId,
      project_name: v.projectName || this.queryProjectName,
      survey_lead: v.surveyLead,
      survey_start_date: v.surveyStartDate,
      survey_end_date: v.surveyEndDate,
      domain: v.domain,
      stage: 'Survey',
      documents: this.documents,
      remarks,
    };
    if (!this.recordId) {
      payload['status'] = 'in_progress';
    }
    return payload;
  }

  private loadProjects(): void {
    this.apiService.getProjectListForDropdown({ limit: 500 }).subscribe({
      next: (res: { data?: SurveyProjectOption[] }) => {
        this.projects = (res?.data || []).map((row) => ({
          id: Number(row.id),
          project_name: row.project_name || `Project #${row.id}`,
          domain: row.domain,
        }));
        this.projectOptions = this.projects.map((p) => ({
          value: p.id,
          label: p.project_name,
        }));
        this.optionsMap['projectOptions'] = this.projectOptions;
        this.applyPendingProject(!this.recordId);
        if (this.recordId) {
          this.surveyForm.get('projectId')?.disable({ emitEvent: false });
        }
      },
      error: () => {
        this.projects = [];
        this.projectOptions = [];
        this.optionsMap['projectOptions'] = [];
        this.toastrMsgService.error('Failed to load projects');
      },
    });
  }

  get projectOptions(): { value: number; label: string }[] {
    return (this.optionsMap['projectOptions'] || []) as { value: number; label: string }[];
  }

  set projectOptions(value: { value: number; label: string }[]) {
    this.optionsMap['projectOptions'] = value;
  }

  onProjectChange(projectId: string | number, syncDomain = true): void {
    const id = Number(projectId);
    if (!Number.isFinite(id) || id <= 0) {
      this.surveyForm.patchValue({ projectName: '' });
      this.lrsRoutes = [];
      this.boqLines = [];
      this.noBoq = false;
      this.boqCode = null;
      return;
    }
    const project = this.projects.find((p) => p.id === id);
    const projectName = project?.project_name || this.queryProjectName || this.surveyForm.get('projectName')?.value || '';
    this.surveyForm.patchValue({
      projectId: id,
      projectName,
    });
    if (syncDomain && project?.domain) {
      this.surveyForm.patchValue({ domain: project.domain });
      this.onDomainChange(String(project.domain));
    }
    this.loadLrsRoutes(String(id));
    this.loadBoqLines(String(id));
  }

  private applyPendingProject(syncDomain: boolean): void {
    const raw = this.surveyForm.getRawValue()?.projectId || this.queryProjectId;
    const id = Number(raw);
    if (!Number.isFinite(id) || id <= 0) return;
    this.onProjectChange(id, syncDomain);
  }

  loadLrsRoutes(projectId?: string): void {
    this.lrsService.listRoutes(projectId ? { project_id: projectId } : undefined).subscribe({
      next: (res) => {
        this.lrsRoutes = res.data || [];
      },
      error: () => {
        this.lrsRoutes = [];
      },
    });
  }

  openSurveyOnMap(): void {
    const v = this.surveyForm.getRawValue();
    const queryParams: Record<string, string | number> = { tab: 'lrs' };
    if (v.lrsRouteId) queryParams['route_id'] = v.lrsRouteId;
    if (v.lrsFromKm != null) queryParams['from_km'] = v.lrsFromKm;
    if (v.lrsToKm != null) queryParams['to_km'] = v.lrsToKm;
    if (v.projectId) queryParams['project_id'] = v.projectId;
    void this.router.navigate(['/home/geospatial'], { queryParams });
  }

  onUploadReport(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.uploading = true;
    this.apiService.upload(file).subscribe({
      next: (res: Record<string, unknown>) => {
        const url = String(
          res?.['url'] ||
          res?.['file_url'] ||
          res?.['path'] ||
          (res?.['data'] as Record<string, unknown>)?.['url'] ||
          (res?.['file'] as Record<string, unknown>)?.['url'] ||
          ''
        );
        if (!url) {
          this.uploading = false;
          this.toastrMsgService.error('Upload succeeded but no file URL was returned');
          return;
        }
        this.documents = [
          ...this.documents,
          { name: file.name, url, uploaded_at: new Date().toISOString() },
        ];
        this.uploading = false;
        this.toastrMsgService.success('Report uploaded');
      },
      error: (err) => {
        this.uploading = false;
        this.toastrMsgService.error(err?.error?.message || 'Upload failed');
      },
    });
  }

  removeDocument(index: number): void {
    this.documents = this.documents.filter((_, i) => i !== index);
  }

  onSubmit(): void {
    const projectId = this.surveyForm.getRawValue().projectId || this.queryProjectId;
    if (projectId && !this.surveyForm.getRawValue().projectId) {
      this.surveyForm.patchValue({
        projectId: Number(projectId),
        projectName: this.surveyForm.getRawValue().projectName || this.queryProjectName,
      });
    }
    if (this.surveyForm.invalid || !this.surveyForm.getRawValue().projectId) {
      this.surveyForm.markAllAsTouched();
      this.toastrMsgService.error('Please fill all required fields');
      return;
    }
    this.saving = true;
    const payload = this.buildPayload();
    const req = this.recordId
      ? this.apiService.updateProjectSurveyStage(this.recordId, payload)
      : this.apiService.saveProjectSurveyStage(payload);

    req.subscribe({
      next: () => {
        this.saving = false;
        this.toastrMsgService.success(this.recordId ? 'Survey updated' : 'Survey saved');
        this.navigateAfterSave();
      },
      error: (err) => {
        this.saving = false;
        this.toastrMsgService.error(err?.error?.message || err?.error?.errorMessage || 'Save failed');
      },
    });
  }

  onReset(): void {
    this.surveyForm.reset();
    this.onDomainChange(this.selectedDomain);
    if (this.recordId) {
      this.surveyForm.get('projectId')?.disable({ emitEvent: false });
    }
    if (this.queryProjectId) {
      this.surveyForm.patchValue({
        projectId: Number(this.queryProjectId),
        projectName: this.queryProjectName,
      });
      this.applyPendingProject(!this.recordId);
    }
  }

  goBack(): void {
    if (this.queryProjectId) {
      this.router.navigate(['/home/survey-list'], {
        queryParams: this.surveyListQueryParams(this.queryProjectId, this.queryProjectName),
      });
      return;
    }
    this.router.navigate(['/home/survey-list']);
  }

  private navigateAfterSave(): void {
    const projectId = this.queryProjectId || this.surveyForm.getRawValue().projectId;
    const projectName = this.queryProjectName || this.surveyForm.getRawValue().projectName;
    if (projectId) {
      this.router.navigate(['/home/survey-list'], {
        queryParams: this.surveyListQueryParams(projectId, projectName),
      });
      return;
    }
    this.router.navigate(['/home/survey-list']);
  }

  private surveyListQueryParams(projectId: string | number, projectName?: string): Record<string, string> {
    const params: Record<string, string> = { projectId: String(projectId) };
    if (projectName) params['projectName'] = String(projectName);
    return params;
  }

  get isRoadDomain(): boolean {
    const domain = this.surveyForm?.get('domain')?.value ?? this.selectedDomain;
    return String(domain).toLowerCase() === 'road';
  }

  onDomainChange(domain: string): void {
    this.selectedDomain = domain;
    if (!this.isRoadDomain && this.surveyForm.contains('lrsRouteId')) {
      this.surveyForm.patchValue({
        lrsRouteId: null,
        lrsFromKm: null,
        lrsToKm: null,
        lrsChainage: '',
      });
    }
    Object.keys(this.surveyForm.controls).forEach((key) => {
      if (
        key !== 'domain' &&
        this.domainFields[domain]?.find((field) => field.key === key) === undefined &&
        Object.values(this.domainFields).flat().some((field) => field.key === key)
      ) {
        this.surveyForm.removeControl(key);
      }
    });
    (this.domainFields[domain] || []).forEach((field) => {
      if (!this.surveyForm.contains(field.key)) {
        this.surveyForm.addControl(field.key, this.fb.control(field.value ?? ''));
      }
    });
  }
}
